// src/components/schedule/WorkingHoursConfig.js
import React, { useState, useEffect } from 'react';
import { systemConfigService } from '../../services/systemConfig';
import { useTranslations } from '../../hooks/useTranslations';
import { useBusinessConfig } from '../../context/BusinessConfigContext';

export function WorkingHoursConfig({ className = '' }) {
  const [workingHours, setWorkingHours] = useState({
    startTime: '08:00',
    endTime: '20:30'
  });
  const [isEditing, setIsEditing] = useState(false);
  const [tempHours, setTempHours] = useState({ startTime: '', endTime: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { settings, updateBusinessConfig } = useBusinessConfig();

  useEffect(() => {
    loadWorkingHours();
  }, [settings]);

  const t = useTranslations();

  const loadWorkingHours = async () => {
    try {
      if (!settings?.workingHours) {
        return;
      }
      const hours = settings.workingHours;
      setWorkingHours(hours);
      setTempHours(hours);
    } catch (error) {
      console.error('Error loading working hours:', error);
      setError('Failed to load working hours');
    }
  };

  const handleEdit = () => {
    setTempHours(workingHours);
    setIsEditing(true);
    setError('');
  };

  const handleCancel = () => {
    setTempHours(workingHours);
    setIsEditing(false);
    setError('');
  };

  const handleSave = async () => {
    if (!tempHours.startTime || !tempHours.endTime) {
      setError('Please select both start and end times');
      return;
    }

    if (tempHours.startTime >= tempHours.endTime) {
      setError('Start time must be before end time');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await updateBusinessConfig({
        'settings.workingHours': {
          startTime: tempHours.startTime,
          endTime: tempHours.endTime
        }
      });
      setWorkingHours(tempHours);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating working hours:', error);
      setError('Failed to update working hours');
    } finally {
      setLoading(false);
    }
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 6; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 10) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    return options;
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('he-IL', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    });
  };

  const timeOptions = generateTimeOptions();

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">{t.workingHours}</h3>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {t.edit}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {error}
        </div>
      )}

      {!isEditing ? (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
          <div className="flex items-center space-x-4">
            <div>
              <span className="text-sm text-gray-500">{t.from}:</span>
              <span className="ml-2 text-sm font-medium text-gray-900">
                {formatTime(workingHours.startTime)}
              </span>
            </div>
            <div className="text-gray-300">—</div>
            <div>
              <span className="text-sm text-gray-500">{t.to}:</span>
              <span className="ml-2 text-sm font-medium text-gray-900">
                {formatTime(workingHours.endTime)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t.startTime}
              </label>
              <select
                value={tempHours.startTime}
                onChange={(e) => setTempHours(prev => ({ ...prev, startTime: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">{t.selectStartTime}</option>
                {timeOptions.map(time => (
                  <option key={time} value={time}>
                    {formatTime(time)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t.endTime}
              </label>
              <select
                value={tempHours.endTime}
                onChange={(e) => setTempHours(prev => ({ ...prev, endTime: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select end time</option>
                {timeOptions.map(time => (
                  <option key={time} value={time}>
                    {formatTime(time)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              {t.cancel}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? t.saving : t.save}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}